/**
 * Canonical API error. Wraps the backend error envelope
 * `{ error: { code, message, details?, request_id? } }` plus the HTTP status,
 * so the UI can branch on `code`/`status` and surface `message` to the user.
 */
export type ApiErrorCode =
  | 'VALIDATION_ERROR'
  | 'AUTH_INVALID_TELEGRAM_DATA'
  | 'UNAUTHORIZED'
  | 'AUTH_USER_NOT_LINKED'
  | 'AUTH_USER_INACTIVE'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONCURRENT_MODIFICATION'
  | 'IDEMPOTENCY_CONFLICT'
  | 'ORDER_INVALID_STATUS'
  | 'STAGE_INVALID_TRANSITION'
  | 'STAGE_PHOTO_REQUIRED'
  | 'STAGE_WRONG_DEPARTMENT'
  | 'INTERNAL'
  | 'NETWORK'

interface ErrorEnvelope {
  error?: {
    code?: string
    message?: string
    details?: Record<string, unknown>
    request_id?: string
  }
}

export class ApiError extends Error {
  readonly status: number
  readonly code: ApiErrorCode
  readonly details?: Record<string, unknown>
  readonly requestId?: string

  constructor(
    status: number,
    code: ApiErrorCode,
    message: string,
    details?: Record<string, unknown>,
    requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.requestId = requestId
  }

  /** Build from an openapi-fetch error body + the raw Response. */
  static from(body: unknown, response: Response): ApiError {
    const env = body as ErrorEnvelope | undefined
    const err = env?.error
    const code = (err?.code as ApiErrorCode | undefined) ?? statusToCode(response.status)
    const message = err?.message ?? defaultMessage(response.status)
    return new ApiError(response.status, code, message, err?.details, err?.request_id)
  }

  static network(cause: unknown): ApiError {
    const message = cause instanceof Error ? cause.message : 'Сеть недоступна'
    return new ApiError(0, 'NETWORK', message)
  }

  get isAuth(): boolean {
    return this.status === 401
  }
  get isConflict(): boolean {
    return this.status === 409
  }
}

function statusToCode(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return 'VALIDATION_ERROR'
    case 401:
      return 'UNAUTHORIZED'
    case 403:
      return 'FORBIDDEN'
    case 404:
      return 'NOT_FOUND'
    case 409:
      return 'CONCURRENT_MODIFICATION'
    case 422:
      return 'ORDER_INVALID_STATUS'
    default:
      return 'INTERNAL'
  }
}

function defaultMessage(status: number): string {
  if (status === 0) return 'Сеть недоступна'
  if (status >= 500) return 'Внутренняя ошибка сервера'
  return `Ошибка запроса (${status})`
}
